/**
 * Chunk upload for session replay.
 *
 * Same shape the bug-report video already uses: ask the backend for a presigned
 * S3 URL, then PUT straight to S3. Replay events never pass through API Gateway
 * — a 30-second chunk of rrweb events is hundreds of KB, and pushing that
 * through the Lambda would put replay traffic in the same error budget, WAF
 * rate counter and p99 as the endpoints we are trying to keep healthy.
 */

/** Gzip if the browser can. Chrome has had CompressionStream since 80, and the
 * primary surface here IS a Chrome extension, so this is the normal path — but
 * the web app has to survive a browser without it. */
async function gzip(text) {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    return await new Response(stream).blob();
  } catch (e) {
    console.log('replay: gzip unavailable, sending plain JSON', e);
    return null;
  }
}

/**
 * How long a single chunk upload gets before it is abandoned.
 *
 * A `fetch` has no timeout of its own — a socket that hangs (clinic Wi-Fi
 * dropping mid-PUT is the normal way this happens) never settles, and the
 * recorder serialises flushes on the in-flight one. Without this bound, one dead
 * socket stops every later flush for the rest of the session while rrweb keeps
 * filling the buffer: the exact unbounded-growth failure the recorder is written
 * to avoid, arriving by a different route. Generous enough for a large chunk on
 * a bad connection, short enough that the next 30s flush is not blocked twice.
 */
const UPLOAD_TIMEOUT_MS = 20000;

/**
 * Upload one chunk of events.
 *
 * `text` is the chunk body already serialised — a JSON array of events. The
 * recorder serialises each event as it arrives (see recorder.onEvent), so
 * re-stringifying here would be a second full pass over the same megabytes.
 *
 * Returns the number of bytes stored, or 0 if the upload failed. Failure is
 * never thrown: a replay is diagnostic data, and a dropped chunk must not
 * surface to the user or break the recorder. Callers keep recording.
 */
export async function uploadChunk(
  apiClient,
  replayId,
  sequence,
  text,
  { keepalive = false, recovered = false } = {},
) {
  const compressed = await gzip(text);
  const contentType = compressed ? 'application/gzip' : 'application/json';
  const body = compressed || new Blob([text], { type: contentType });

  // Browsers reject a keepalive fetch whose body exceeds 64 KB — and reject it
  // by throwing, which would lose the chunk we were specifically trying to save.
  // Past that size the ordinary request is the better bet: it may be cancelled
  // on unload, but it at least has a chance.
  const useKeepalive = keepalive && (body.size || text.length) <= 64 * 1024;

  // AbortController, not Promise.race: racing would let the request go on
  // running (and holding the connection) after we stopped waiting for it.
  // Skipped for keepalive requests, whose whole purpose is to outlive the page —
  // aborting one on unload would defeat the point, and the browser bounds them.
  const controller = useKeepalive ? null : new AbortController();
  const timer = controller
    ? setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)
    : null;

  try {
    const { url } = await apiClient.getReplayChunkUrl(replayId, sequence, contentType, 'events', recovered);
    if (!url) return 0;

    const response = await fetch(url, {
      method: 'PUT',
      // Must match the ContentType the backend signed with, byte for byte, or
      // S3 rejects the signature.
      headers: { 'Content-Type': contentType },
      body,
      keepalive: useKeepalive,
      signal: controller ? controller.signal : undefined,
    });
    if (!response.ok) {
      console.log(`replay: chunk ${sequence} rejected by S3 (${response.status})`);
      return 0;
    }
    return body.size || text.length;
  } catch (e) {
    // Includes the AbortError from the timeout above. Same handling either way:
    // the chunk is lost, the recorder carries on.
    console.log(`replay: chunk ${sequence} upload failed`, e);
    return 0;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
